import { useNavigate } from 'react-router-dom';

import { Notice } from '@/components/Modal';
import { holidayWorkLabel, useHolidayWorks, useMyOvertime } from '@/pages/mypage/useHolidayWorks';

/**
 * 시간외근무 — 내 야근·휴일근무 기록이 모이는 칸 (사용자 지시 2026-10-14 · V446·V450).
 *
 * <h2>신청은 「시간외근무 신청」 하나다</h2>
 * 일요일·법정공휴일 정상 출근이면 승인 뒤 <b>휴일근무</b>로 서고, 대체휴무 신청(연차 사용 서식)에서
 * 그 근무를 골라 하루를 쉰다. 평일 야근은 시간이 쌓이고 8시간마다 「연차 전환 신청」 으로 인사총무팀
 * 결재를 받아 연차 1일이 된다. 남은 일수·시간은 서버가 결재 상태로 센다 — 여기서 따로 세지 않는다.
 */
export function SubstituteLeavePanel() {
  const navigate = useNavigate();
  const holidays = useHolidayWorks();
  const overtime = useMyOvertime();
  const works = holidays.data ?? [];
  const weekday = overtime.data;

  const draft = (formKey: string, data: Record<string, unknown> = {}) =>
    navigate(`/approval/draft?formKey=${formKey}&data=` + encodeURIComponent(JSON.stringify(data)));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <button className="btn-secondary" style={compactButton} onClick={() => draft('OVERTIME')}>
          시간외근무 신청
        </button>
      </div>

      <section className="space-y-2">
        <h3 style={sectionTitle}>휴일근무 · 대체휴무</h3>
        {holidays.isPending ? (
          <div className="flex justify-center py-6"><div className="spinner w-6 h-6" /></div>
        ) : holidays.error ? (
          <Notice tone="error">불러오는 중입니다</Notice>
        ) : works.length === 0 ? (
          <p className="py-4 text-center" style={mutedText}>승인된 휴일근무가 없습니다.</p>
        ) : (
          works.map((work) => (
            <div key={work.id} className="rounded-lg flex items-center gap-3 flex-wrap" style={row}>
              <span style={{ fontSize: '13px', fontWeight: 700 }}>{holidayWorkLabel(work)}</span>
              <span style={mutedText}>남은 {work.remaining}일 · {work.expiresOn} 까지</span>
              <span className={`badge ${work.status === '사용 가능' ? 'badge-blue' : 'badge-gray'}`}>{work.status}</span>
              {/* 소멸·사용 완료된 근무에는 신청 버튼을 두지 않는다(사용자 지시 2026-10-14 — 「소멸이라며 왜 보이냐」). */}
              {work.status === '사용 가능' && (
                <button
                  className="btn-secondary ml-auto"
                  style={compactButton}
                  onClick={() => draft('LEAVE', { leaveType: '대체휴무', holidayWorkIds: [work.id] })}
                >
                  대체휴무 신청
                </button>
              )}
            </div>
          ))
        )}
      </section>

      <section className="space-y-2">
        <h3 style={sectionTitle}>평일 시간외근무</h3>
        {overtime.isPending ? (
          <div className="flex justify-center py-6"><div className="spinner w-6 h-6" /></div>
        ) : overtime.error || !weekday ? (
          <Notice tone="error">불러오는 중입니다</Notice>
        ) : (
          <>
            <div className="rounded-lg flex items-center gap-4 flex-wrap" style={row}>
              <span style={{ fontSize: '13px' }}>누적 <b>{weekday.totalHours}시간</b></span>
              <span style={{ fontSize: '13px' }}>전환 <b>{weekday.convertedHours}시간</b></span>
              <span style={{ fontSize: '13px' }}>남은 <b>{weekday.availableHours}시간</b></span>
              <span style={{ fontSize: '13px' }}>전환 가능 <b>{weekday.convertibleDays}일</b></span>
              {/* 전환할 일수 바로 옆에 둔다(사용자 지시 2026-10-14). 전환할 것이 없으면 버튼도 없다. */}
              {weekday.convertibleDays > 0 && (
                <button
                  className="btn-secondary"
                  style={compactButton}
                  onClick={() => draft('OVERTIME_CONVERT', {
                    days: String(weekday.convertibleDays),
                    // 남은 시간이 있는 날을 미리 체크해 둔다 — 신청서에서 빼고 넣을 수 있다.
                    overtimeHourIds: weekday.hours.filter((entry) => entry.hours > entry.used).map((entry) => entry.id),
                  })}
                >
                  연차 전환 신청
                </button>
              )}
            </div>
            {weekday.hours.length === 0 ? (
              <p className="py-4 text-center" style={mutedText}>승인된 평일 시간외근무가 없습니다.</p>
            ) : (
              weekday.hours.map((entry) => (
                <div key={entry.id} className="rounded-lg flex items-center gap-3" style={row}>
                  <span style={{ fontSize: '13px', fontWeight: 700 }}>{entry.workDate}</span>
                  <span style={mutedText}>{entry.hours}시간 · 잔여 {Math.max(0, entry.hours - entry.used)}시간</span>
                </div>
              ))
            )}
            {weekday.conversions.map((conversion) => (
              <div key={conversion.id} className="rounded-lg flex items-center gap-3" style={row}>
                <span style={{ fontSize: '13px', fontWeight: 700 }}>연차 전환 {conversion.days}일</span>
                <span style={mutedText}>{conversion.hours}시간</span>
                <span className={`badge ${conversion.status === 'COMPLETED' ? 'badge-blue' : 'badge-gray'}`}>
                  {conversion.status === 'COMPLETED' ? '부여됨' : '결재 중'}
                </span>
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
}

const compactButton = { fontSize: '12px', padding: '5px 12px' } as const;
const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
const sectionTitle = { fontSize: '13px', fontWeight: 700 } as const;
const row = { border: '1px solid var(--border)', padding: '10px 12px' } as const;
